'use client';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useRef, Suspense } from 'react';
import NProgress from 'nprogress';

function ProgressBarContent() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const isNavigating = useRef(false);

  useEffect(() => {
    // Override router.push to show progress
    const originalPush = router.push;
    router.push = (...args) => {
      if (!isNavigating.current) {
        isNavigating.current = true;
        NProgress.start();
      }
      return originalPush.apply(router, args);
    };

    const handleLinkClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest('a[href]') as HTMLAnchorElement;

      if (
        link &&
        link.href &&
        !link.hasAttribute('download') &&
        !link.href.startsWith('mailto:') &&
        !link.href.startsWith('tel:') &&
        !link.href.startsWith('javascript:') &&
        !link.href.startsWith('blob:')
      ) {
        try {
          const url = new URL(link.href);
          if (
            url.origin === window.location.origin &&
            url.pathname !== pathname &&
            !isNavigating.current
          ) {
            isNavigating.current = true;
            NProgress.start();
          }
        } catch {
          // Invalid URL, ignore
        }
      }
    };

    document.addEventListener('click', handleLinkClick);

    return () => {
      router.push = originalPush;
      document.removeEventListener('click', handleLinkClick);
    };
  }, [pathname, router]);

  useEffect(() => {
    NProgress.done();
    isNavigating.current = false;
  }, [pathname, searchParams]);

  return null;
}

export default function ProgressBar() {
  return (
    <Suspense fallback={null}>
      <ProgressBarContent />
    </Suspense>
  );
}
